import Select from "@/components/Select";
import React from "react";
import { SerializedCondition } from "../types";
import useTeams from "@/hooks/useTeams";

/**
 * TeamAgentAvailabilityCondition — evaluates whether a SPECIFIC team has
 * available agents. Used by pre-conversation workflows (widget_loaded,
 * chat_button_clicked) to hide the widget, serve a different chat window,
 * or route elsewhere when the target team is offline/unavailable.
 *
 * Distinct from TeamAvailabilityCondition (which uses the conversation's team).
 */
export type TeamAgentAvailabilityCondition = {
  teamId: number;
  status: "Available" | "Unavailable" | "Busy";
  comparator: "equals" | "notEquals";
};

const COMPARATOR_LABELS: Record<string, string> = {
  equals: "is",
  notEquals: "is not"
};

const STATUS_LABELS: Record<string, string> = {
  Available: "online (any agent available)",
  Unavailable: "offline (no agents available)",
  Busy: "busy (all agents on calls/chats)"
};

export function TeamAgentAvailabilityEditor({
  condition,
  onUpdate
}: {
  condition: SerializedCondition;
  onUpdate: (condition: SerializedCondition) => void;
}) {
  const { teams } = useTeams();
  const data = JSON.parse(
    condition.data || "{}"
  ) as Partial<TeamAgentAvailabilityCondition>;

  const teamOptions = (Array.isArray(teams) ? teams : []).reduce(
    (acc, t) => {
      acc[t.id.toString()] = t.name;
      return acc;
    },
    {} as Record<string, string>
  );

  function update(patch: Partial<TeamAgentAvailabilityCondition>) {
    onUpdate({ ...condition, data: JSON.stringify({ ...data, ...patch }) });
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs text-slate-500 whitespace-nowrap">team</span>
      <Select
        options={{ "": "— pick team —", ...teamOptions }}
        value={data.teamId?.toString() ?? ""}
        onChange={(v) => update({ teamId: Number(v) })}
      />
      <Select
        options={COMPARATOR_LABELS}
        value={data.comparator ?? "equals"}
        onChange={(v) =>
          update({ comparator: v as TeamAgentAvailabilityCondition["comparator"] })
        }
      />
      <Select
        options={STATUS_LABELS}
        value={data.status ?? "Available"}
        onChange={(v) =>
          update({ status: v as TeamAgentAvailabilityCondition["status"] })
        }
      />
    </div>
  );
}
